/* eslint-disable max-lines */
import { type BenefitType, BenefitTypeOptions, BenefitTypes } from '@strata/constants';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { FormSectionWithRows } from '@strata/react-form-section';
import { TextInputFormik } from '@strata/react-inputs';
import { Spinner } from '@strata/react-loading-animation';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { lookupMemberData, postNewReferral } from 'api/oopsAPI';
import {
    BrandGenericDefault,
    type MemberSelection,
    MemberSelectionOptions,
    MemberSelections,
    type MemberType,
    MemberTypeOptions,
    MemberTypes,
} from 'services/constants';
import { handleError } from 'services/Error';
import {
    useGetDiagnosisListQuery,
    useLazyGetMemberListQuery,
    useLazyGetProductListQuery,
} from 'store/api/oopsApi';
import {
    useGetPayerSegmentListQuery,
    useGetPlaceOfServiceOptionsQuery,
    useGetPrescriberListQuery,
    useGetSupplierListQuery,
} from 'store/api/referenceApi';

const ValidationSchema = Yup.object({
    segment_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    payer_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    benefit: Yup.string()
        .oneOf(Object.values(BenefitTypes))
        .required(validationStrings.requiredField.errorMessage),
    product_oid: Yup.string().required(validationStrings.requiredField.errorMessage),
    diagnosis_code: Yup.string(),
    memberType: Yup.string()
        .oneOf(Object.values(MemberTypes))
        .required(validationStrings.requiredField.errorMessage),
    member_id: Yup.string(),
    patient_first_name: Yup.string(),
    patient_dob: DateInputFormik.validator(),
    supplier_npi: Yup.string(),
    prescriber_npi: Yup.string(),
    place_of_service: Yup.string(),
    auth_start: DateInputFormik.validator(),
    auth_expiration: DateInputFormik.validator(),
    service_start_date: DateInputFormik.validator(),
    referral_start: DateInputFormik.validator(),
    referral_expiration: DateInputFormik.validator(),
    is_brand: Yup.bool().required(validationStrings.requiredField.errorMessage).nullable(),
    grandfathered: Yup.bool().required(validationStrings.requiredField.errorMessage),
    toc_flag: Yup.bool().required(validationStrings.requiredField.errorMessage),
});

const InitialValues = {
    memberType: MemberTypes.TEST,
    benefit: BenefitTypes.PHARMACY,
    is_brand: null,
    grandfathered: false,
    toc_flag: false,
} as Yup.InferType<typeof ValidationSchema>;

export const TestReferral = () => {
    const [generatedReferral, setGeneratedReferral] = useState<object>();

    const { data: payerSegmentList, isFetching: payerSegmentListFetching } =
        useGetPayerSegmentListQuery();
    const { data: placeOfServiceOptions, isFetching: placeOfServiceOptionsFetching } =
        useGetPlaceOfServiceOptionsQuery();
    const { data: supplierList, isFetching: supplierListFetching } = useGetSupplierListQuery();
    const { data: prescriberList, isFetching: prescriberListFetching } =
        useGetPrescriberListQuery();

    const { data: diagnosisList, isFetching: diagnosisListFetching } = useGetDiagnosisListQuery();
    const diagnosesCodeOptions = useMemo(
        () =>
            (diagnosisList ?? [])
                .map((diagnosis) =>
                    diagnosis.codes.map((code) => ({
                        name: code.description,
                        secondaryData: code.code,
                        oid: diagnosis.oid,
                    })),
                )
                .flat()
                .sort((a, b) => sortAsc(a.name, b.name)),
        [diagnosisList],
    );

    const [selectedPayerId, setSelectedPayerId] = useState<string>();
    const [selectedSegmentId, setSelectedSegmentId] = useState<string>();
    const [selectedBenefit, setSelectedBenefit] = useState<BenefitType>(InitialValues.benefit);
    const [includeInactiveProducts, setIncludeInactiveProducts] = useState(false);

    const [getProductList, { data: productList, isFetching: productListFetching }] =
        useLazyGetProductListQuery();
    useEffect(() => {
        void (async () => {
            if (selectedSegmentId && selectedBenefit) {
                await getProductList({
                    segmentId: selectedSegmentId,
                    benefit: selectedBenefit,
                    includeInactive: includeInactiveProducts,
                });
            }
        })();
    }, [selectedSegmentId, selectedBenefit, includeInactiveProducts, getProductList]);

    const productOptions = useMemo(
        () =>
            (productList ?? []).map((product) => ({
                ...product,
                secondaryData: product.isActive ? product.secondaryData : 'Inactive',
            })),
        [productList],
    );

    const [memberSelection, setMemberSelection] = useState<MemberSelection>(MemberSelections.NEW);
    const [selectedMemberType, setSelectedMemberType] = useState<MemberType>(
        InitialValues.memberType,
    );

    const [getMemberList, { data: memberList, isFetching: memberListFetching }] =
        useLazyGetMemberListQuery();
    useEffect(() => {
        void (async () => {
            if (selectedPayerId && selectedMemberType) {
                await getMemberList({ payerId: selectedPayerId, memberType: selectedMemberType });
            }
        })();
    }, [selectedPayerId, selectedMemberType, getMemberList]);

    return (
        <Formik
            initialValues={InitialValues}
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                try {
                    const response = await postNewReferral({
                        segment_id: values.segment_id,
                        payer_id: values.payer_id,
                        billing: values.benefit,
                        product_oid: values.product_oid,
                        diagnosis_code: values.diagnosis_code,
                        memberType: values.memberType,
                        member_id: values.member_id,
                        patient_first_name: values.patient_first_name,
                        patient_dob: formatDate(values.patient_dob, DateFormats.HYPHEN_DATE),
                        supplier_npi: values.supplier_npi,
                        prescriber_npi: values.prescriber_npi,
                        place_of_service: values.place_of_service,
                        auth_start: formatDate(values.auth_start, DateFormats.HYPHEN_DATE),
                        auth_expiration: formatDate(
                            values.auth_expiration,
                            DateFormats.HYPHEN_DATE,
                        ),
                        service_start_date: formatDate(
                            values.service_start_date,
                            DateFormats.HYPHEN_DATE,
                        ),
                        referral_start: formatDate(values.referral_start, DateFormats.HYPHEN_DATE),
                        referral_expiration: formatDate(
                            values.referral_expiration,
                            DateFormats.HYPHEN_DATE,
                        ),
                        is_brand: values.is_brand,
                        grandfathered: values.grandfathered,
                        toc_flag: values.toc_flag,
                    });
                    SuccessToast({ message: 'Test referral created.' });
                    setGeneratedReferral(response);
                } catch (err) {
                    setGeneratedReferral({});
                    handleError('Error creating test referral.', err as Error);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Create Test Referral"
                    primaryButtonLabel="Create Referral"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onCancelClick={formik.resetForm}
                    formLeftContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="segment_id"
                                label="Payer Segment"
                                required
                                isLoading={payerSegmentListFetching}
                                values={payerSegmentList}
                                selectedItem={payerSegmentList?.find(
                                    ({ segmentId }) => segmentId === formik.values.segment_id,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue(
                                            'segment_id',
                                            newValue?.segmentId,
                                        );
                                        await formik.setFieldValue('payer_id', newValue?.payerId);
                                        setSelectedSegmentId(newValue?.segmentId);
                                        setSelectedPayerId(newValue?.payerId);
                                    })();
                                }}
                            />
                            <RadioButtonGroupFormik
                                name="benefit"
                                label="Benefit"
                                required
                                inline
                                options={BenefitTypeOptions}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue('benefit', newValue);
                                        setSelectedBenefit(newValue as BenefitType);
                                    })();
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="product_oid"
                                label="Product"
                                required
                                isLoading={productListFetching}
                                values={productOptions}
                                selectedItem={productOptions.find(
                                    ({ oid }) => oid === formik.values.product_oid,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('product_oid', newValue?.oid);
                                }}
                            />
                            <CheckboxListFormik
                                name="includeInactiveProducts"
                                options={['Include inactive products']}
                                values={
                                    includeInactiveProducts ? ['Include inactive products'] : []
                                }
                                onChange={(newVal) => {
                                    const showInactive = newVal?.length === 1;
                                    setIncludeInactiveProducts(showInactive);
                                    if (
                                        !showInactive &&
                                        productList?.some(
                                            (product) =>
                                                !product.isActive &&
                                                product.oid === formik.values.product_oid,
                                        )
                                    ) {
                                        void formik.setFieldValue('product_oid', undefined);
                                    }
                                }}
                            />
                            <RadioButtonGroupFormik
                                name="is_brand"
                                label="Product Brand/Generic"
                                inline
                                options={BrandGenericDefault.map(({ name, value }) => ({
                                    text: name,
                                    value: String(value),
                                }))}
                                selectedValue={String(formik.values.is_brand)}
                                onChange={(newValue) => {
                                    void formik.setFieldValue(
                                        'is_brand',
                                        newValue === 'true' || newValue === 'false'
                                            ? newValue === 'true'
                                            : null,
                                    );
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="supplier_npi"
                                label="Supplier NPI"
                                isLoading={supplierListFetching}
                                values={supplierList}
                                selectedItem={supplierList?.find(
                                    ({ npi }) => npi === formik.values.supplier_npi,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('supplier_npi', newValue?.npi);
                                }}
                            />
                            <RadioButtonGroupFormik
                                name="memberSelection"
                                label="Member Selection"
                                required
                                inline
                                options={MemberSelectionOptions}
                                selectedValue={memberSelection}
                                onChange={(newValue) => {
                                    void (async () => {
                                        setMemberSelection(newValue as MemberSelection);
                                        await formik.setFieldValue('memberSelection', newValue);
                                        await formik.setFieldValue('member_id', undefined);
                                        await formik.setFieldValue('patient_first_name', undefined);
                                        await formik.setFieldValue('patient_dob', undefined);
                                    })();
                                }}
                            />
                            <RadioButtonGroupFormik
                                name="memberType"
                                label="Member Type"
                                required
                                inline
                                options={MemberTypeOptions}
                                disabled={memberSelection !== MemberSelections.EXISTING}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue('memberType', newValue);
                                        setSelectedMemberType(newValue as MemberType);
                                    })();
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="existingMember"
                                label="Existing Member"
                                isLoading={memberListFetching}
                                values={memberList}
                                placeholderText="Select An Existing"
                                disabled={
                                    memberSelection !== MemberSelections.EXISTING ||
                                    selectedPayerId === undefined
                                }
                                selectedItem={memberList?.find(
                                    ({ memberId, firstName, dateOfBirth }) =>
                                        memberId === formik.values.member_id &&
                                        firstName === formik.values.patient_first_name &&
                                        dayjs(dateOfBirth).isSame(formik.values.patient_dob),
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue('member_id', newValue?.memberId);
                                        await formik.setFieldValue(
                                            'patient_first_name',
                                            newValue?.firstName,
                                        );
                                        await formik.setFieldValue(
                                            'patient_dob',
                                            newValue?.dateOfBirth,
                                        );
                                    })();
                                }}
                            />
                            <TextInputFormik
                                id="member_id"
                                label="Member ID"
                                disabled={memberSelection !== MemberSelections.LOOKUP}
                            />
                            <TextInputFormik
                                id="patient_first_name"
                                label="Member First Name"
                                disabled={memberSelection !== MemberSelections.LOOKUP}
                            />
                            <DateInputFormik
                                id="patient_dob"
                                label="Member Date Of Birth"
                                disabled={memberSelection !== MemberSelections.LOOKUP}
                            />
                            <Button
                                intent={Button.Intent.PRIMARY}
                                disabled={memberSelection !== MemberSelections.LOOKUP}
                                label="Lookup Member"
                                onClick={() => {
                                    void (async () => {
                                        if (
                                            formik.values.payer_id &&
                                            formik.values.member_id &&
                                            formik.values.patient_first_name &&
                                            formik.values.patient_dob
                                        ) {
                                            const memberDetails = await lookupMemberData({
                                                payerId: formik.values.payer_id,
                                                memberId: formik.values.member_id,
                                                firstName: formik.values.patient_first_name,
                                                dob: formatDate(
                                                    formik.values.patient_dob,
                                                    DateFormats.HYPHEN_DATE,
                                                ),
                                            });
                                            if (Object.keys(memberDetails).length > 1) {
                                                SuccessToast({ message: 'Member found.' });
                                            } else {
                                                ErrorToast({ message: 'Member not found.' });
                                            }
                                        }
                                    })();
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="prescriber_npi"
                                label="Prescriber NPI"
                                isLoading={prescriberListFetching}
                                values={prescriberList}
                                selectedItem={prescriberList?.find(
                                    ({ prescriberNpi }) =>
                                        prescriberNpi === formik.values.prescriber_npi,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue(
                                        'prescriber_npi',
                                        newValue?.prescriberNpi,
                                    );
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="place_of_service"
                                label="Place of Service"
                                values={placeOfServiceOptions}
                                isLoading={placeOfServiceOptionsFetching}
                                selectedItem={placeOfServiceOptions?.find(
                                    ({ code }) => code === formik.values.place_of_service,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('place_of_service', newValue?.code);
                                }}
                            />
                            <DateInputFormik id="auth_start" label="Auth Start Date" />
                            <DateInputFormik id="auth_expiration" label="Auth Expiration Date" />
                            <DateInputFormik id="service_start_date" label="Service Start Date" />
                            <DateInputFormik id="referral_start" label="Referral Start" />
                            <DateInputFormik id="referral_expiration" label="Referral Expiration" />
                            <FormSectionWithRows
                                sectionTitle="Referral Options"
                                width="100%"
                                rowOneFlexDirection="column"
                                rowOneContent={
                                    <>
                                        <div className="flex__gap--x-large">
                                            <CheckboxListFormik
                                                name="grandfathered"
                                                required
                                                options={['Grandfathered']}
                                                values={
                                                    formik.values.grandfathered
                                                        ? ['Grandfathered']
                                                        : []
                                                }
                                                onChange={(newVal) => {
                                                    void formik.setFieldValue(
                                                        'grandfathered',
                                                        newVal?.length === 1,
                                                    );
                                                }}
                                            />
                                            <CheckboxListFormik
                                                name="toc_flag"
                                                required
                                                options={['TOC Flag']}
                                                values={formik.values.toc_flag ? ['TOC Flag'] : []}
                                                onChange={(newVal) => {
                                                    void formik.setFieldValue(
                                                        'toc_flag',
                                                        newVal?.length === 1,
                                                    );
                                                }}
                                            />
                                        </div>
                                        <SuggestSingleSelectFormik
                                            id="diagnosis_code"
                                            label="Diagnosis"
                                            isLoading={diagnosisListFetching}
                                            values={diagnosesCodeOptions}
                                            selectedItem={diagnosesCodeOptions.find(
                                                ({ secondaryData }) =>
                                                    secondaryData === formik.values.diagnosis_code,
                                            )}
                                            onChange={(newValue) => {
                                                void formik.setFieldValue(
                                                    'diagnosis_code',
                                                    newValue?.secondaryData,
                                                );
                                            }}
                                        />
                                    </>
                                }
                            />
                        </>
                    }
                    formRightContent={
                        <>
                            <div>Generated Referral:</div>
                            {formik.isSubmitting ? (
                                <Spinner intent={Spinner.Intent.NONE} />
                            ) : (
                                <pre>
                                    {generatedReferral &&
                                        JSON.stringify(generatedReferral, null, 2)}
                                </pre>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
